import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { ProtectedRoute } from '@/modules/auth/ProtectedRoute';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuthStore } from '@/store/auth.store';

const LoginPage = lazy(() =>
  import('@/modules/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/modules/auth/RegisterPage').then((m) => ({
    default: m.RegisterPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/modules/dashboard/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  })),
);
const UsersPage = lazy(() =>
  import('@/modules/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const AreasPage = lazy(() =>
  import('@/modules/areas/AreasPage').then((m) => ({ default: m.AreasPage })),
);
const AssignAreaPage = lazy(() =>
  import('@/modules/assign-area/AssignAreaPage').then((m) => ({
    default: m.AssignAreaPage,
  })),
);
const AttendancePage = lazy(() =>
  import('@/modules/attendance/AttendancePage').then((m) => ({
    default: m.AttendancePage,
  })),
);
const MyAttendancePage = lazy(() =>
  import('@/modules/attendance/MyAttendancePage').then((m) => ({
    default: m.MyAttendancePage,
  })),
);
const AttendanceReportsPage = lazy(() =>
  import('@/modules/attendance/AttendanceReportsPage').then((m) => ({
    default: m.AttendanceReportsPage,
  })),
);
const TicketsPage = lazy(() =>
  import('@/modules/tickets/TicketsPage').then((m) => ({
    default: m.TicketsPage,
  })),
);
const InventoryPage = lazy(() =>
  import('@/modules/inventory/InventoryPage').then((m) => ({
    default: m.InventoryPage,
  })),
);
const ItemMasterPage = lazy(() =>
  import('@/modules/inventory/ItemMasterPage').then((m) => ({
    default: m.ItemMasterPage,
  })),
);
const ItemStockPage = lazy(() =>
  import('@/modules/inventory/ItemStockPage').then((m) => ({
    default: m.ItemStockPage,
  })),
);
const DispatchesPage = lazy(() =>
  import('@/modules/inventory/DispatchesPage').then((m) => ({
    default: m.DispatchesPage,
  })),
);
const ReturnsPage = lazy(() =>
  import('@/modules/inventory/ReturnsPage').then((m) => ({
    default: m.ReturnsPage,
  })),
);
const DestroyItemPage = lazy(() =>
  import('@/modules/inventory/DestroyItemPage').then((m) => ({
    default: m.DestroyItemPage,
  })),
);
const DestroyHistoryPage = lazy(() =>
  import('@/modules/inventory/DestroyHistoryPage').then((m) => ({
    default: m.DestroyHistoryPage,
  })),
);
const HubStockReportPage = lazy(() =>
  import('@/modules/inventory/reports/HubStockReportPage').then((m) => ({
    default: m.HubStockReportPage,
  })),
);
const AreaStockReportPage = lazy(() =>
  import('@/modules/inventory/reports/AreaStockReportPage').then((m) => ({
    default: m.AreaStockReportPage,
  })),
);
const ItemStockReportPage = lazy(() =>
  import('@/modules/inventory/reports/ItemStockReportPage').then((m) => ({
    default: m.ItemStockReportPage,
  })),
);
const UsageHistoryReportPage = lazy(() =>
  import('@/modules/inventory/reports/UsageHistoryReportPage').then((m) => ({
    default: m.UsageHistoryReportPage,
  })),
);
const ItemLedgerReportPage = lazy(() =>
  import('@/modules/inventory/reports/ItemLedgerReportPage').then((m) => ({
    default: m.ItemLedgerReportPage,
  })),
);
const ItemTrackerDetailPage = lazy(() =>
  import('@/modules/inventory/ItemTrackerDetailPage').then((m) => ({
    default: m.ItemTrackerDetailPage,
  })),
);
const CustomersPage = lazy(() =>
  import('@/modules/customers/CustomersPage').then((m) => ({
    default: m.CustomersPage,
  })),
);
const ProfilePage = lazy(() =>
  import('@/modules/profile/ProfilePage').then((m) => ({
    default: m.ProfilePage,
  })),
);
const ChangePasswordPage = lazy(() =>
  import('@/modules/profile/ChangePasswordPage').then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/modules/settings/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
);
const NotFoundPage = lazy(() =>
  import('@/modules/common/NotFoundPage').then((m) => ({
    default: m.NotFoundPage,
  })),
);

function PageFallback() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-1/2" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-2xl" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-2xl" />
    </div>
  );
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const isAuthed = useAuthStore((s) => s.isAuthenticated());
  if (isAuthed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              <PublicOnly>
                <LoginPage />
              </PublicOnly>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnly>
                <RegisterPage />
              </PublicOnly>
            }
          />
        </Route>

        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/users"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/areas"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <AreasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assign-area"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <AssignAreaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/me"
            element={
              <ProtectedRoute roles={['ADMIN', 'SUB_ADMIN', 'SUPPORT', 'AGENT', 'VENDOR']}>
                <MyAttendancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <AttendancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/reports"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <AttendanceReportsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route
            path="/inventory/items"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <ItemMasterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/stock"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <ItemStockPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/dispatches"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <DispatchesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/returns"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <ReturnsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/destroy"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <DestroyItemPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/destroy/history"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <DestroyHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/reports"
            element={<Navigate to="/inventory/reports/hub" replace />}
          />
          <Route
            path="/inventory/reports/hub"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <HubStockReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/reports/area"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <AreaStockReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/reports/item"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <ItemStockReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/reports/usage"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <UsageHistoryReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/reports/ledger"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <ItemLedgerReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/item/:id"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <ItemTrackerDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <InventoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute roles={['OWNER', 'ADMIN', 'SUB_ADMIN']}>
                <CustomersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute roles={['OWNER']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
